import {
  Button,
  DatePicker,
  Field,
  FieldError,
  Label,
  RadioGroup,
} from "@/components/ui";
import {
  FileInput,
  FileUploader,
  FileUploaderContent,
  FileUploaderItem,
} from "@/components/ui/FileUpload";
import { SubmitCancelButtons } from "@/features/Patient/MedicalRecords/components/SubmitCancelButtons";
import { formatUtcDateTime } from "@/lib/time";
import { Controller, type UseFormReturn } from "react-hook-form";
import type { z } from "zod";
import { useUploadResultsReport } from "../../network/useUploadResultsReport";
import type { diagnosticReportFormSchema } from "../../schema";
export const UploadResultsReport = ({
  form,
  patientId,
  serviceRequestId,
  onCancel,
  closeForm,
}: {
  form: UseFormReturn<z.infer<typeof diagnosticReportFormSchema>>;
  patientId: string;
  serviceRequestId: string;
  onCancel?: () => void;
  closeForm?: () => void;
}) => {
  const { mutate: uploadResultsReport, isPending: isUploadingResultsReport } =
    useUploadResultsReport({
      patientId,
      serviceRequestId,
      onSuccess: () => {
        form.reset();
        closeForm?.();
      },
    });

  const filesValue = form.watch("files");
  const reportStatusValue = form.watch("reportStatus");
  const reportReceivedDateValue = form.watch("reportReceivedDate");

  const onSubmit = () => {
    if (!filesValue || !reportStatusValue) return;

    uploadResultsReport({
      file: filesValue[0],
      reportReceivedDate: formatUtcDateTime(
        reportReceivedDateValue.toISOString(),
      ),
      status: reportStatusValue,
    });
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={form.handleSubmit(onSubmit, (errors) => {
        console.info(errors);
      })}
    >
      <Controller
        control={form.control}
        name="files"
        render={({ field, fieldState }) => (
          <Field className="col-span-2">
            <Label htmlFor="files">Results Report PDF</Label>
            <FileUploader
              value={field.value}
              onValueChange={(files) => field.onChange(files ?? [])}
              dropzoneOptions={{
                accept: {
                  "application/pdf": [".pdf"],
                },
                maxFiles: 1,
                multiple: false,
              }}
            >
              {field.value.length === 0 && (
                <FileInput className="border border-dashed border-link pl-2">
                  <Button variant="link">
                    Click or Drop Results Report PDF
                  </Button>
                </FileInput>
              )}
              {field.value.length > 0 && (
                <FileUploaderContent>
                  <FileUploaderItem index={0}>
                    {field.value[0]?.name}
                  </FileUploaderItem>
                </FileUploaderContent>
              )}
            </FileUploader>
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      <Controller
        control={form.control}
        name="reportReceivedDate"
        render={({ field, fieldState }) => (
          <Field className="w-fit">
            <DatePicker
              date={field.value}
              setDate={(date) => {
                field.onChange(new Date(date.toUTCString()));
              }}
              dateFormat="MM/dd/yyyy"
              closeOnSelect={true}
              label="Report Received Date"
              disableFuture
            />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      <Controller
        control={form.control}
        name="reportStatus"
        render={({ field, fieldState }) => (
          <Field>
            <Label htmlFor="reportStatus">
              Does the report indicate partial or final results?
            </Label>
            <RadioGroup
              value={field.value}
              onValueChange={(value) => field.onChange(value)}
              options={[
                { label: "Partial", value: "partial" },
                { label: "Final", value: "final" },
              ]}
            />
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />

      <div className="flex justify-start">
        <SubmitCancelButtons
          isSaving={isUploadingResultsReport}
          onCancel={onCancel}
        />
      </div>
    </form>
  );
};
